import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {ArrowBack, Edit, FormatListBulleted, InsertLinkRounded} from "@mui/icons-material";
import {UploadStates, WarrantyAndReturnMessageTypes, WarrantyAndReturnRequestStatuses} from "../../../../../../core/constants/enums";
import IconButton from "../../../../../components/base/button/icon_button";
import {createUUId} from "../../../../../../core/services/utils/utils";
import useWindowSize from "../../../../../hooks/use-window/size";
import Api from "../../../../../../core/services/api/api";
import WarrantyAndReturnSingleUserTab from "./user-tab";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import WarrantyAndReturnSingleVendorTab from "./vendor-tab";
import EditPartSupportRequestDialog from "../../../../../components/dialogs/upsert-part-support-request/edit";
import TryAgain from "../../../../../components/app-specific/try-again";
import AddPartSupportRequestDialog, {AddPartSupportRequestProcessSteps} from "../../../../../components/dialogs/upsert-part-support-request/add";
import ToastService from "../../../../../../core/services/toast";
import {SvgIcon, Tooltip} from "@mui/material";
import {routeFunctions} from "../../../../../../core/models/static/routes/app-routes";
import {ReactComponent as Printer} from "../../../../../../assets/images/printer.svg";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import {ReactComponent as DriverCheckListIcon} from "../../../../../../assets/images/part-support-driver-checklist.svg";
import PartSupportAdditionalInformationDialog from "../../../../../components/dialogs/part-support-additional-information";
import PartSupportDriverChecklistDialog from "../../../../../components/dialogs/part-support-driver-checklist";
import PartSupportExtraInfoLinksManagementDialog from "../../../../../components/dialogs/part-support-extra-info-links-management";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import ElementIds from "../../../../../../core/models/element-ids";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import useQuery from "../../../../../hooks/use-query";
import {Link, useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const tabs = {
    customer: 'Customer',
    vendor: 'Vendor',
};


export const ChatMessageOperationTypes = {
    add: 'add',
    remove: 'remove',
}

/**
 * Calculates the height of the inner chats so that the elements stay on the page. The minimum height of the inner
 * chat is half of the main sections' height.
 * @return {number}
 */
const getHeight = () => {
    let res = document.getElementById(ElementIds.panel.main)?.clientHeight ?? 0;
    res -= document.getElementById(ElementIds.panel.appBar)?.scrollHeight ?? 0;
    res -= (document.getElementById(ElementIds.partSupportChat.marginBottom)?.scrollHeight ?? 0) + 30;
    res -= document.getElementById(ElementIds.partSupportChat.marginTop)?.scrollHeight ?? 0;
    res -= document.getElementById(ElementIds.partSupportChat.header)?.scrollHeight ?? 0;
    res -= document.getElementById(ElementIds.partSupportChat.footer)?.scrollHeight ?? 0;
    res -= document.getElementById(ElementIds.partSupportChat.tabs)?.scrollHeight ?? 0;
    res = Math.max(res, ((document.getElementById(ElementIds.panel.main)?.clientHeight ?? 0) / 2));
    res -= (20 + 65); // 80 for the margins
    return res;
}

const initialExtraInfoLinksManagementDialogState = {
    open: false,
    requestId: undefined,
}

const WarrantyAndReturnSingle = () => {
    const operatorAccessLevels = useAccessLevels();
    const [query, setQuery] = useQuery();
    const history = useHistory();
    const params = useParams();
    const [loading, setLoading] = useState(true);
    const [loadingTypes, setLoadingTypes] = useState(true);
    const [userChats, setUserChats] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [types, setTypes] = useState([]);
    const [request, setRequest] = useState({});
    const [height, setHeight] = useState(getHeight);
    const [editRequest, setEditRequest] = useState({open: false, request: undefined});
    const [addRequest, setAddRequest] = useState({open: false, initialProcessInfo: undefined});
    const [openAdditionInfoDialog, setOpenAdditionInfoDialog] = useState(false);
    const [openDriverForm, setOpenDriverForm] = useState(false);
    const [extraInfoLinksManagementDialogState, setExtraInfoLinksManagementDialogState] = useState(initialExtraInfoLinksManagementDialogState);
    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);
    const isMounted = useIsMounted();
    const windowSize = useWindowSize();
    const topMarginRef = useRef();
    const bottomMarginRef = useRef();
    const chatRef = useRef();
    const {configureNotebookTags, toggleShow} = useConfigureNotebook('warranty-and-return-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const tab = useMemo(() => query?.tab ?? tabs.customer, [query]);
    const tabRef = useRef(tab);
    tabRef.current = tab;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        if (!request?.rga) {
            return toggleShow(false);
        }
        toggleShow(true);
        configureNotebookTags((noteTags) => noteTags.warrantyAndReturnInformation(params.requestId, request.rga));
        configureBehavioralTool((tags) => tags.warrantyAndReturnInformation(params.requestId, request.rga));
    }, [configureBehavioralTool, configureNotebookTags, params.requestId, request.rga, toggleShow]);

    /**
     * Listens for the changes in the reference current of all sections in this page except for the body and the
     * changes in the size of the window:
     * for each change, if all currents exist, calculates the new height of the body and if it is not the same as
     * the current height, sets the new height as the current height.
     */
    useEffect(() => {
        if (!topMarginRef?.current
            || !bottomMarginRef?.current
            || !chatRef?.current?.headerRef
            || !chatRef?.current?.footerRef) {
            return;
        }
        setHeight((height) => {
            const newHeight = getHeight();
            if (newHeight !== height) {
                return newHeight;
            }
            return height;
        })
    }, [chatRef.current?.headerRef, chatRef.current?.footerRef, windowSize, loading, request]);

    /**
     * Fetches the statuses and types of the requests from the server and if the component is mounted, and the api
     * result is
     * successful, then sets the statuses.
     * @type {(function(): Promise<void>)}
     */
    const getStatusesAndTypes = useCallback(async () => {
        setLoadingTypes(true);
        const response = await Api.getPartSupportTypesAndStatuses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setStatuses(response.data?.statuses);
            setTypes(response.data?.types);
        }
        setLoadingTypes(false);
    }, [isMounted])


    /**
     * Changes the tab of the url query
     * @type {(function(*): void)}
     */
    const onTabChange = useCallback((newValue) => {
        setQuery(prevState => ({...prevState, tab: newValue}), {replace: true});
    }, [setQuery])

    /**
     * Scrolls the chat section to the message being operated on.
     * @type {(function(*=, *=): void)}
     */
    const scrollInCaseOfChatChange = useCallback((message, type) => {
        if (!message || !chatRef?.current?.bodyRef) return;
        const chatSection = document.getElementsByClassName('chat-section-scroller')?.item(0);
        if (!chatSection) return;
        switch (type) {
            case ChatMessageOperationTypes.add:
                chatSection.scrollTo({left: 0, top: chatSection.scrollHeight})
                break;
            case ChatMessageOperationTypes.remove:
                break;
            default:
                break;
        }
    }, [])

    /**
     * Fetches the request information from the server, and  request chats already sent and received from the server
     * and if the result of the api is successful, sets the request info.
     * @type {(function(): Promise<void>)}
     */
    const getRequestInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getPartSupportRequestInformation(params.requestId, true);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRequest(response?.data
                ? {
                    ...response?.data,
                    removingCredit: false,
                    removingVendorCredit: false,
                    removingVendor: false,
                    accepting: false,
                } : response?.data
            );
            setUserChats(response?.data?.messages?.map(e => e.typeID === WarrantyAndReturnMessageTypes.image
                ? ({
                    ...e,
                    _reactKey: `_${createUUId(true)}`,
                    uploadState: {state: UploadStates.done},
                    imageUploadState: {state: UploadStates.done}
                })
                : ({
                    ...e,
                    _reactKey: `_${createUUId(true)}`,
                    uploadState: {state: UploadStates.done}
                })
            ));
            if (response?.data?.messages?.length) {
                setTimeout(() => {
                    if (!isMounted()) return;
                    scrollInCaseOfChatChange(response.data.messages[response.data.messages.length - 1], ChatMessageOperationTypes.add);
                }, 600)
            }
        }
        // in case of api error or when the request does not have an assigned vendor
        if ((!response?.resultFlag || !response?.data?.vendor) && tabRef.current === tabs.vendor) {
            onTabChange(tabs.users);
        }
        setLoading(false);
    }, [params.requestId, isMounted, scrollInCaseOfChatChange, onTabChange])


    /**
     * Fetches the request information from the server when the component is mounted.
     */
    useEffect(() => {
        void getRequestInformation();
    }, [getRequestInformation])

    /**
     * Fetches the statuses and types of the requests from the server when the component is mounted.
     */
    useEffect(() => {
        void getStatusesAndTypes();
    }, [getStatusesAndTypes])

    /**
     * Fetches the makes and models from the server and sets them in the state.
     * @type {(function(): Promise<void>)}
     */
    const getMakesAndModels = useCallback(async () => {
        setLoading(true);
        const response = await Api.getAllMakesAndModelsList();
        if (!isMounted()) return;
        setLoading(false);
        if (response?.resultFlag) {
            setMakes(response?.data?.makesList ?? {});
            setModels(response?.data?.modelsList ?? {});
        }
    }, [isMounted])

    /**
     * Fetches the makes and models from the server when the component is mounted.
     */
    useEffect(() => {
        void getMakesAndModels();
    }, [getMakesAndModels])

    /**
     * Removes the request from the server and if the operation is successful, navigates the operator back.
     */
    const removeRequest = useCallback(async () => {
        const response = await Api.removeWarrantyAndReturnRequest(request.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            history.goBack();
        }
    }, [request?.id, isMounted, history])


    /**
     * Opens the edit request dialog.
     */
    const openEditRequest = useCallback(() => {
        setEditRequest(prevState => ({
            ...prevState,
            open: true,
            request: request,
        }))
    }, [request])

    /**
     * Opens the additional information dialog
     */
    const openAdditionalInfoDialog = useCallback(() => {
        setOpenAdditionInfoDialog(true);
    }, [])

    /**
     * Handles updating the part support request additional info with received values from dialog
     */
    const onAdditionInformationUpdated = useCallback((additionalInfo) => {
        setRequest(prevState => ({...prevState, ...additionalInfo}));
    }, [])

    /**
     * Opens the driver form dialog
     */
    const openDriverFormDialog = useCallback(() => {
        setOpenDriverForm(true);
    }, [])

    /**
     * Edits the current request with the information of the new request.
     * @type {(function(*): void)}
     */
    const onRequestEdited = useCallback((newRequest) => {
        if (newRequest) {
            void getRequestInformation();
        }
    }, [getRequestInformation])

    /**
     * Closes the edit request dialog.
     * @type {function(): void}
     */
    const closeEditRequestDialog = useCallback(() => setEditRequest({open: false, request: undefined}), [])

    /**
     * Closes the add request dialog.
     * @type {function(): void}
     */
    const closeAddRequestDialog = useCallback(() => setAddRequest({open: false, initialProcessInfo: undefined}), [])

    /**
     * Opens the documentation view so the operator can proceed with the submission of the docs in the request.
     */
    const openDocumentation = useCallback(() => {
        if (!request.customer && !request.warehouseCode) {
            return ToastService.error({
                title: "No Customer Found",
                message: "The selected request does not belong to any customers. Please contact IT support.",
            });
        }
        if (!operatorAccessLevels.packages.warrantyAndReturn.submitRequest) {
            return ToastService.error({
                title: "Access Denied",
                message: "This request requires you to submit documentations for it, that you do not have access to.",
            });
        }
        setAddRequest({
            open: true,
            initialProcessInfo: {
                currentProcess: AddPartSupportRequestProcessSteps.documentation,
                request: request,
            }
        })
    }, [request, operatorAccessLevels.packages.warrantyAndReturn.submitRequest])

    /**
     * Sets the properties of the request.
     * @param request
     * @param newProperties
     */
    const setRequestProperty = useCallback((_, newProperties = {}) => {
        setRequest(prevState => ({...prevState, ...newProperties}) ?? {})
    }, [])

    /**
     * Gets users confirmation before removing the current part support request.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this part support request? You may not be able to undo this action.
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeRequest(),
        );
    }

    /**
     * Opens the dialog for managing the extra info links of the request.
     * @type {(function(): void)}
     */
    const openExtraInfoLinksManagement = useCallback(() => {
        setExtraInfoLinksManagementDialogState({
            open: true,
            requestId: request?.id,
        });
    }, [request?.id])

    return (
        <>
            <div
                ref={topMarginRef}
                id={ElementIds.partSupportChat.marginTop}
                className='row no-gutters w-100 justify-content-between align-items-center'
            >
                <div className={'d-flex align-items-center'}>
                    <IconButton
                        onPressed={() => history.goBack()}
                        color={'paper.secondary'}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <h2 className={'text-xl font-weight-semi-bold mb-0 ml-2'}>
                        Part Support Request Information
                    </h2>
                </div>
                <div className={'d-flex align-items-center justify-content-end mt-2 mt-md-0'}>
                    {
                        operatorAccessLevels.packages.warrantyAndReturn.extraRequestInformation &&
                        <Tooltip title={'Extra Info Links Management'} className={'mr-2'}>
                            <span>
                                <IconButton
                                    onPressed={openExtraInfoLinksManagement}
                                    disabled={loading}
                                    color={'paper.secondary'}
                                    className={'email-icon-container'}
                                >
                                    <InsertLinkRounded className='icon medium'/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.warrantyAndReturn.drivers &&
                        <Tooltip title={'Driver Form'} className={'mr-2'}>
                            <span>
                                <IconButton
                                    onPressed={openDriverFormDialog}
                                    disabled={loading}
                                    color={'paper.secondary'}
                                    className={'email-icon-container'}
                                >
                                    <SvgIcon className='icon medium' component={DriverCheckListIcon}/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.warrantyAndReturn.extraRequestInformation &&
                        <Tooltip title={'Additional Info'} className={'mr-2'}>
                        <span>
                            <IconButton
                                onPressed={openAdditionalInfoDialog}
                                disabled={loading}
                                color={'paper.secondary'}
                                className={'email-icon-container'}
                            >
                                <FormatListBulleted className='icon medium'/>
                            </IconButton>
                        </span>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.bizPrint.printWarrantyAndReturnClaim &&
                        <Tooltip title={'Print'} className={'mr-2'}>
                                <span>
                                    <IconButton
                                        disabled={loading}
                                        color={'paper.secondary'}
                                        className={'email-icon-container'}
                                        component={Link}
                                        to={routeFunctions.print.partSupport(params.requestId)}
                                        target={'_blank'}
                                    >
                                    <SvgIcon className='icon medium' component={Printer}/>
                                </IconButton>
                                </span>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                        <Tooltip title={'Edit'} className={'mr-2'}>
                            <span>
                                <IconButton
                                    onPressed={openEditRequest}
                                    disabled={loading}
                                    color={'paper.secondary'}
                                    className={'email-icon-container'}
                                >
                                    <Edit className={'icon medium'}/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    }
                    {
                        operatorAccessLevels.packages.warrantyAndReturn.editRequest &&
                        <Tooltip title={'Remove'}>
                        <span>
                              <IconButton
                                  disabled={loading}
                                  onPressed={getRemoveConfirmation}
                                  color={'paper.error'}
                                  className={'email-icon-container'}
                              >
                                  <SvgIcon component={RemoveIcon} className='icon large'/>
                              </IconButton>
                        </span>
                        </Tooltip>
                    }
                </div>
            </div>
            {
                request?.statusID === WarrantyAndReturnRequestStatuses.initial
                    ? <>
                        <div className={'my-5 py-5'}>
                            <TryAgain
                                text={"You must provide the necessary documents to be able to proceed to the chat to the" +
                                    " customer or vendor. To do so, you can click below"}
                                buttonText={'See document templates'}
                                showButton={true}
                                onClick={openDocumentation}
                            />
                        </div>
                    </>
                    : <>
                        <div className='w-100 flex-grow-1 flex-column d-flex'>
                            {
                                (operatorAccessLevels.packages.warrantyAndReturn.manageVendor || operatorAccessLevels.packages.warrantyAndReturn.chatWithVendor) &&
                                <Tabs id={ElementIds.partSupportChat.tabs} value={tab} onChange={(e, i) => onTabChange(i)}
                                >
                                    <Tab value={tabs.customer} label={tabs.customer}/>
                                    <Tab value={tabs.vendor} label={tabs.vendor}/>
                                </Tabs>
                            }
                            <div className='my-2'/>
                        </div>
                        {
                            tab === tabs.customer &&
                            <WarrantyAndReturnSingleUserTab
                                models={models}
                                makes={makes}
                                loading={loading}
                                height={height}
                                request={request}
                                statuses={statuses}
                                chats={userChats}
                                setChats={setUserChats}
                                scrollInCaseOfChatChange={scrollInCaseOfChatChange}
                                setRequestProperty={setRequestProperty}
                                ref={chatRef}
                            />
                        }
                        {
                            tab === tabs.vendor &&
                            <WarrantyAndReturnSingleVendorTab
                                loading={loading}
                                height={height}
                                request={request}
                                statuses={statuses}
                                setRequest={setRequest}
                                scrollInCaseOfChatChange={scrollInCaseOfChatChange}
                                setRequestProperty={setRequestProperty}
                                ref={chatRef}
                                makes={makes}
                                models={models}
                            />
                        }
                        <div id={ElementIds.partSupportChat.marginBottom} ref={bottomMarginRef} className={'m-100 py-2'}/>
                    </>
            }
            <AddPartSupportRequestDialog
                open={addRequest.open}
                setOpen={closeAddRequestDialog}
                types={types}
                loadingTypes={loadingTypes}
                onSuccess={onRequestEdited}
                request={addRequest.initialProcessInfo?.request}
                currentProcess={addRequest.initialProcessInfo?.currentProcess}
            />
            <EditPartSupportRequestDialog
                open={editRequest.open}
                setOpen={closeEditRequestDialog}
                request={editRequest.request}
                onSuccess={onRequestEdited}
                types={types}
                loadingTypes={loadingTypes}
            />
            <PartSupportAdditionalInformationDialog
                open={openAdditionInfoDialog}
                setOpen={setOpenAdditionInfoDialog}
                onSuccess={onAdditionInformationUpdated}
                makes={makes}
                models={models}
                additionalInformation={{
                    makeId: request?.makeId,
                    modelId: request?.modelId,
                    year: request?.year,
                    vin: request?.vin,
                    installedDate: request?.installedDate,
                    failedDate: request?.failedDate,
                    installedMileage: request?.installedMileage,
                    failedMileage: request?.failedMileage,
                    remarks: request?.remarks,
                }}
                requestId={params.requestId}
            />
            <PartSupportDriverChecklistDialog
                open={openDriverForm}
                setOpen={setOpenDriverForm}
                requestId={params.requestId}
            />
            <PartSupportExtraInfoLinksManagementDialog
                open={extraInfoLinksManagementDialogState.open}
                setOpen={() => setExtraInfoLinksManagementDialogState(initialExtraInfoLinksManagementDialogState)}
                requestId={extraInfoLinksManagementDialogState.requestId}
            />
        </>
    );
}


export default WarrantyAndReturnSingle;
